import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Modal,
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  IconButton,
  Paper,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomDate from "../../../../CustomForm/CustomDate";
import CustomInput from "../../../../FormFields/CustomInput/CustomInput";
import {
  contentStyle,
  headerStyle,
  modalStyle,
  questionBoxStyle,
  questionContentStyle,
  questionHeaderStyle,
} from "./environmentalAssessment.config";
import { createEnvironmentalRiskAssessment } from "../../../../../api/services/assessment";
import { useSnackbar } from "notistack";
import { useQueryClient } from "react-query";
import { QK } from "../../../../../base/qk";
import { SignatureModal } from "../../../../SignaturePad/SignatureModal";

export default function CreateEnvironmentalRiskAssessmentModal({
  open,
  handleClose,
  questions = [],
  clientName = "",
}) {
  const { id: clientId } = useParams();

  const [formData, setFormData] = useState({
    assessor_name: "",
    assessor_signature: "",
    date_completed: new Date().toISOString().split("T")[0],
    next_review_date: "",
    action_required: "",
    address: "",
    answers: {},
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSaveSignature = (signatureData) => {
    console.log(signatureData);
    setFormData((prevData) => ({
      ...prevData,
      assessor_signature: signatureData,
    }));
    setIsSignatureModalOpen(false);
  };

  const handleAnswerChange = (questionId, field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      answers: {
        ...prevData.answers,
        [questionId]: {
          ...prevData.answers[questionId],
          [field]: value,
        },
      },
    }));
  };

  const handleCloseModal = () => {
    setSubmitError(null);
    resetForm();
    handleClose();
  };

  const resetForm = () => {
    setFormData({
      assessor_name: "",
      assessor_signature: "",
      date_completed: new Date().toISOString().split("T")[0],
      next_review_date: "",
      action_required: "",
      address: "",
      answers: {},
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData?.assessor_signature) {
      enqueueSnackbar("Signature cannot be blank", { variant: "error" });
      return;
    }
    setSubmitError(null);
    setIsSubmitting(true);

    const answersPayload = Object.keys(formData?.answers).map((questionId) => ({
      question: parseInt(questionId, 10),
      answer: formData?.answers[questionId]?.answer || "NA",
      note: formData?.answers[questionId]?.note || "",
    }));

    const assessmentPayload = {
      client: clientId,
      assessor_name: formData?.assessor_name,
      assessor_signature: formData?.assessor_signature,
      date_completed: formData?.date_completed,
      next_review_date: formData?.next_review_date || null,
      action_required: formData?.action_required,
      address: formData?.address,
      answers: answersPayload,
    };

    try {
      const response =
        await createEnvironmentalRiskAssessment(assessmentPayload);

      if (response.status === 201) {
        enqueueSnackbar("Environmental Risk Assessment Added", {
          variant: "success",
        });
        queryClient.invalidateQueries(QK.environmentalAssessments);
        resetForm();
        handleCloseModal();
      }
    } catch (err) {
      setSubmitError(err.message);
      enqueueSnackbar("Failed to create", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      aria-labelledby="create-risk-assessment-modal-title"
    >
      <Box sx={modalStyle}>
        <Box sx={headerStyle}>
          <Typography
            id="create-risk-assessment-modal-title"
            variant="h5"
            component="h2"
            fontWeight={600}
          >
            Environmental Risk Assessment for {clientName}
          </Typography>

          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={contentStyle} component="form" onSubmit={handleSubmit}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight={600} sx={{ flexGrow: 1 }}>
                Assessment Details
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <CustomInput
                  name="assessor_name"
                  label="Assessor Name"
                  value={formData?.assessor_name}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <CustomDate
                  name="date_completed"
                  label="Date Completed"
                  value={formData?.date_completed}
                  onChange={handleChange}
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <CustomDate
                  name="next_review_date"
                  label="Next Review Date"
                  value={formData?.next_review_date}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <CustomInput
                  name="address"
                  label="Assessment Address"
                  value={formData?.address}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="body2"
                  component="label"
                  sx={{ display: "block", mb: 1, color: "text.secondary" }}
                >
                  Assessor Signature *
                </Typography>
                <Box
                  sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setIsSignatureModalOpen(true)}
                    sx={{ fontSize: "0.875rem", flexShrink: 0 }}
                  >
                    {formData.assessor_signature ? "Change" : "Add Signature"}
                  </Button>
                  {formData.assessor_signature && (
                    <Box
                      sx={{
                        p: 0.5,
                        border: "1px solid #e0e0e0",
                        borderRadius: 0.5,
                        width: 120,
                        bgcolor: "grey.50",
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={formData.assessor_signature}
                        alt="Signature"
                        style={{ width: "100%", display: "block" }}
                      />
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Paper
            elevation={0}
            sx={{ p: 3, mb: 3, border: "1px solid #e0e0e0" }}
          >
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Risk Assessment Questions
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {questions.length === 0 && !isSubmitting ? (
              <Typography color="text.secondary" textAlign="center" py={4}>
                No questions provided.
              </Typography>
            ) : (
              questions.map((question, index) => (
                <Box key={question.id} sx={questionBoxStyle}>
                  <Box sx={questionHeaderStyle}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="primary"
                      sx={{ minWidth: "24px" }}
                    >
                      {index + 1}.
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {question.question_text}
                    </Typography>
                  </Box>

                  <Box sx={questionContentStyle}>
                    <FormControl component="fieldset" fullWidth>
                      <RadioGroup
                        row
                        aria-label={`answer for question ${question.id}`}
                        name={`question-${question.id}`}
                        value={formData?.answers[question.id]?.answer || ""}
                        onChange={(e) =>
                          handleAnswerChange(
                            question.id,
                            "answer",
                            e.target.value
                          )
                        }
                        sx={{ mb: 2 }}
                      >
                        <FormControlLabel
                          value="YES"
                          control={<Radio size="small" />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="NO"
                          control={<Radio size="small" />}
                          label="No"
                        />
                        <FormControlLabel
                          value="NA"
                          control={<Radio size="small" />}
                          label="N/A"
                        />
                      </RadioGroup>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Notes"
                      variant="outlined"
                      size="small"
                      value={formData?.answers[question.id]?.note || ""}
                      onChange={(e) =>
                        handleAnswerChange(question.id, "note", e.target.value)
                      }
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Box>
              ))
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{ p: 3, mb: 3, border: "1px solid #e0e0e0" }}
          >
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Action Required
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <CustomInput
              name="action_required"
              label="Action Required / Further Information"
              value={formData?.action_required}
              onChange={handleChange}
              multiline
              rows={4}
            />
          </Paper>

          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {submitError}
            </Alert>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              onClick={handleCloseModal}
              variant="outlined"
              sx={{ px: 4, py: 1 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              sx={{ px: 4, py: 1 }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Submitting...
                </>
              ) : (
                "Submit Assessment"
              )}
            </Button>
          </Box>
        </Box>
        <SignatureModal
          open={isSignatureModalOpen}
          handleClose={() => setIsSignatureModalOpen(false)}
          onSave={handleSaveSignature}
        />
      </Box>
    </Modal>
  );
}
